import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { FileText, Save, Eye, ExternalLink, CheckCircle2, AlertCircle, RefreshCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SLUG_LABELS = {
  returns: { tkey: 'admin.legal_page.slug_returns', fallback: 'Vrátenie tovaru a reklamácie', icon: '↩' },
  shipping: { tkey: 'admin.legal_page.slug_shipping', fallback: 'Doprava a doručenie', icon: '🚚' },
  privacy: { tkey: 'admin.legal_page.slug_privacy', fallback: 'Ochrana osobných údajov', icon: '🔒' },
  terms: { tkey: 'admin.legal_page.slug_terms', fallback: 'Obchodné podmienky', icon: '📄' },
};

// Substitute {{seller.field}} placeholders the same way the backend does so
// the live preview matches the public page byte-for-byte.
const renderTemplate = (text, ctx) => {
  if (!text) return '';
  return text.replace(/\{\{\s*seller\.([a-zA-Z_]+)\s*\}\}/g, (_, field) => {
    const v = ctx[field];
    if (v === undefined || v === null || v === '') return `{{seller.${field}}}`;
    // Multi-line values become Markdown soft breaks
    return String(v).includes('\n') ? String(v).replace(/\n/g, '  \n') : String(v);
  });
};

const LANGS = [
  { code: 'sk', label: '🇸🇰 SK' },
  { code: 'cs', label: '🇨🇿 CS' },
  { code: 'de', label: '🇩🇪 DE' },
  { code: 'en', label: '🇬🇧 EN' },
  { code: 'ru', label: '🇷🇺 RU' },
];

const AdminLegalPages = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [sellerContext, setSellerContext] = useState({});
  const [sellerFields, setSellerFields] = useState([]);
  const [activeSlug, setActiveSlug] = useState('returns');
  const [activeLang, setActiveLang] = useState('sk');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [draft, setDraft] = useState({ title: '', content: '' });

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/legal-pages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPages(data.pages || []);
        setSellerContext(data.seller_context || {});
        setSellerFields(data.seller_fields || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPages(); /* eslint-disable-next-line */ }, []);

  // Sync draft when slug or lang changes
  useEffect(() => {
    const page = pages.find((p) => p.slug === activeSlug);
    const tr = page?.translations?.[activeLang] || { title: '', content: '' };
    setDraft({ title: tr.title || '', content: tr.content || '' });
    setSavedAt(null);
    setError('');
  }, [activeSlug, activeLang, pages]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/legal-pages/${activeSlug}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          translations: {
            [activeLang]: { title: draft.title.trim(), content: draft.content },
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Save failed');
        return;
      }
      setSavedAt(new Date());
      // Replace the page in our state with the fresh server copy
      setPages((prev) => prev.map((p) => (p.slug === activeSlug ? data : p)));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    const info = SLUG_LABELS[activeSlug];
    const pageName = info ? t(info.tkey, info.fallback) : activeSlug;
    if (!window.confirm(
      t('admin.legal_page.reset_confirm', 'Vrátiť stránku „{{name}}" na pôvodný šablón? Všetky úpravy vo všetkých jazykoch sa stratia.', { name: pageName }),
    )) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/legal-pages/${activeSlug}/reset-to-default`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Reset failed');
        return;
      }
      setSavedAt(new Date());
      setPages((prev) => prev.map((p) => (p.slug === activeSlug ? data : p)));
      // Refresh the editor draft from the newly reset content
      const tr = data?.translations?.[activeLang] || { title: '', content: '' };
      setDraft({ title: tr.title || '', content: tr.content || '' });
    } finally {
      setSaving(false);
    }
  };

  const activePage = pages.find((p) => p.slug === activeSlug);

  return (
    <div className="p-4 sm:p-6" data-testid="admin-legal-pages-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FileText className="h-7 w-7 text-orange-500" />
          <h1 className="text-2xl font-bold text-gray-900">
            {t('admin.legal.title', 'Právne stránky')}
          </h1>
        </div>
        <a
          href={`/legal/${activeSlug}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700"
        >
          <ExternalLink className="h-4 w-4" /> {t('admin.legal_page.open_public', 'Otvoriť verejnú stránku')}
        </a>
      </div>

      {/* Slug tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(SLUG_LABELS).map(([slug, info]) => (
          <button
            key={slug}
            onClick={() => setActiveSlug(slug)}
            data-testid={`legal-slug-tab-${slug}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition border ${
              activeSlug === slug
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
            }`}
          >
            <span className="mr-2">{info.icon}</span>
            {t(info.tkey, info.fallback)}
          </button>
        ))}
      </div>

      {/* Language tabs */}
      <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b">
        {LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => setActiveLang(l.code)}
            data-testid={`legal-lang-tab-${l.code}`}
            className={`px-3 py-1.5 rounded text-sm transition ${
              activeLang === l.code
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">Editor</h2>
              <button
                onClick={() => setShowPreview((s) => !s)}
                className="lg:hidden inline-flex items-center gap-1 text-xs text-orange-600"
              >
                <Eye className="h-3 w-3" /> {showPreview ? t('admin.legal_page.hide', 'Skryť') : t('admin.legal_page.preview', 'Náhľad')}
              </button>
            </div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.legal_page.field_title', 'Názov')}</label>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-4"
              data-testid="legal-title-input"
            />
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Obsah (Markdown)
            </label>
            <textarea
              rows={22}
              value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
              data-testid="legal-content-input"
            />
            {sellerFields.length > 0 && (
              <details className="mt-3 text-xs text-gray-600">
                <summary className="cursor-pointer hover:text-orange-600 select-none">
                  💡 {t('admin.legal_page.vars_hint', 'Dostupné premenné z firemných nastavení (klikni pre rozbalenie)')}
                </summary>
                <div className="mt-2 p-3 bg-gray-50 rounded text-xs space-y-1 font-mono">
                  {sellerFields.map((f) => (
                    <div key={f} className="flex items-baseline gap-2">
                      <code className="text-orange-600">{`{{seller.${f}}}`}</code>
                      <span className="text-gray-500">→</span>
                      <span className="text-gray-700 truncate">
                        {(sellerContext[f] || '').replace(/\n/g, ' · ') || t('admin.legal_page.empty_var', '(prázdne)')}
                      </span>
                    </div>
                  ))}
                  <div className="pt-2 mt-2 border-t border-gray-200 text-gray-500">
                    Premenné sa nahradia automaticky pri zobrazení verejnej stránky. Kontaktné údaje (meno, email, telefón, adresa) sa čerpajú zo stránky <a href="/admin/contact" className="text-orange-600 underline">Kontakt</a>; účtovné polia (IČO, DIČ, IBAN) z <a href="/admin/company-settings" className="text-orange-600 underline">Firemné nastavenia</a>.
                  </div>
                </div>
              </details>
            )}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {savedAt && (
                  <span className="inline-flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    Uložené {savedAt.toLocaleTimeString('sk-SK')}
                  </span>
                )}
                {error && (
                  <span className="inline-flex items-center gap-1 text-red-600">
                    <AlertCircle className="h-3 w-3" /> {error}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={resetToDefault}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 text-sm"
                  data-testid="legal-reset-btn"
                  title={t('admin.legal_page.reset_tip', 'Obnoviť pôvodný šablón (všetky jazyky)')}
                >
                  <RefreshCcw className="h-4 w-4" />
                  {t('admin.legal_page.reset_template', 'Obnoviť šablón')}
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 text-sm font-medium"
                  data-testid="legal-save-btn"
                >
                  <Save className="h-4 w-4" />
                  {saving ? t('admin.legal_page.saving', 'Ukladám…') : t('admin.legal_page.save_lang', 'Uložiť {{lang}}', { lang: activeLang.toUpperCase() })}
                </button>
              </div>
            </div>
          </div>

          {/* Live preview (with seller placeholders rendered) */}
          <div className={`${showPreview ? '' : 'hidden lg:block'} bg-white rounded-lg shadow border border-gray-200 p-4`}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">{t('admin.legal_page.preview', 'Náhľad')}</h2>
              <span className="text-xs text-gray-400">{t('admin.legal_page.vars_replaced', 'premenné nahradené z firemných nastavení')}</span>
            </div>
            <article className="prose max-w-none legal-prose">
              <h1 className="!mt-0">{renderTemplate(draft.title, sellerContext) || t('admin.legal_page.no_title', '(bez názvu)')}</h1>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {renderTemplate(draft.content, sellerContext) || ''}
              </ReactMarkdown>
            </article>
          </div>
        </div>
      )}

      {activePage?.updated_at && (
        <div className="mt-4 text-xs text-gray-400">
          Posledná aktualizácia: {new Date(activePage.updated_at).toLocaleString('sk-SK')}
          {activePage.updated_by && ` · ${activePage.updated_by}`}
        </div>
      )}
    </div>
  );
};

export default AdminLegalPages;
